import { useState } from "react";
import { products } from "@/lib/products";
import { ArrowUpRight, Info, ShoppingBag } from "lucide-react";
import { PRODUCTS_BUY_URL } from "@/lib/translations";
import ProductDetailDialog from "./ProductDetailDialog";

export default function ProductsGrid({ t, language }) {
  const [active, setActive] = useState(null);

  return (
    <section
      id="products"
      data-testid="products-section"
      className="py-24 md:py-32"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-14">
          <div>
            <p className="text-overline">{t.explorer.productsOverline}</p>
            <h2 className="font-serif font-light tracking-tight leading-[1.05] text-4xl md:text-5xl mt-4 text-[#1a2421] max-w-2xl">
              {t.explorer.productsTitle}
            </h2>
            <p className="mt-5 text-lg leading-relaxed text-[#5c6b61] max-w-xl">
              {t.explorer.productsBody}
            </p>
          </div>
          <a
            href={PRODUCTS_BUY_URL}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="see-all-products-btn"
            className="inline-flex items-center gap-2 text-sm font-semibold uppercase tracking-[0.18em] text-[#bc6c25] hover:text-[#9c581e]"
          >
            {t.explorer.seeAllProducts}
            <ArrowUpRight className="w-4 h-4" />
          </a>
        </div>

        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {products.map((p, i) => (
            <article
              key={p.slug}
              data-testid={`product-${p.slug}`}
              className="group bg-white rounded-3xl overflow-hidden lift border border-[#2c4a3b]/8 flex flex-col"
            >
              <button
                type="button"
                onClick={() => setActive(p)}
                className="aspect-[4/3] overflow-hidden bg-white relative w-full"
                aria-label={`Ver ficha de ${p.name[language]}`}
              >
                <img
                  src={p.officialImage || p.image}
                  alt={p.name[language]}
                  className={`absolute inset-0 w-full h-full object-contain p-6 transition-opacity duration-500 ${
                    p.sachetImage ? "group-hover:opacity-0" : ""
                  }`}
                  loading={i < 3 ? "eager" : "lazy"}
                />
                {p.sachetImage && (
                  <img
                    src={p.sachetImage}
                    alt=""
                    aria-hidden
                    className="absolute inset-0 w-full h-full object-contain p-6 opacity-0 transition-opacity duration-500 group-hover:opacity-100"
                    loading="lazy"
                  />
                )}
              </button>
              <div className="p-6 md:p-7 flex flex-col flex-1">
                <h3 className="font-serif text-2xl text-[#1a2421] leading-snug">
                  {p.name[language]}
                </h3>
                <p className="mt-2 text-sm text-[#5c6b61] leading-relaxed">
                  {p.tagline[language]}
                </p>
                <ul className="mt-5 space-y-2 flex-1">
                  {p.benefits[language].slice(0, 3).map((b, j) => (
                    <li
                      key={j}
                      className="text-sm text-[#1a2421]/85 flex gap-2 leading-snug"
                    >
                      <span className="text-[#bc6c25] shrink-0">·</span>
                      {b}
                    </li>
                  ))}
                </ul>
                <div className="mt-6 grid grid-cols-2 gap-2 border-t border-[#2c4a3b]/10 pt-5">
                  <button
                    type="button"
                    onClick={() => setActive(p)}
                    data-testid={`view-${p.slug}`}
                    className="inline-flex items-center justify-center gap-1.5 rounded-full border border-[#2c4a3b]/30 text-[#2c4a3b] hover:bg-[#2c4a3b]/5 text-xs font-semibold py-2.5 px-3 transition-colors"
                  >
                    <Info className="w-3.5 h-3.5" />
                    {language === "es" ? "Ver ficha" : "View details"}
                  </button>
                  <a
                    href={PRODUCTS_BUY_URL}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid={`buy-${p.slug}`}
                    className="inline-flex items-center justify-center gap-1.5 rounded-full bg-[#bc6c25] hover:bg-[#9c581e] text-white text-xs font-bold py-2.5 px-3 transition-colors"
                  >
                    <ShoppingBag className="w-3.5 h-3.5" />
                    {language === "es" ? "Comprar" : "Buy"}
                  </a>
                </div>
              </div>
            </article>
          ))}
        </div>
      </div>

      <ProductDetailDialog
        product={active}
        open={!!active}
        onOpenChange={(v) => !v && setActive(null)}
        t={t}
        language={language}
      />
    </section>
  );
}
